import React, { useState, useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { api } from '../services/api';
import { useFeatures } from '../contexts/FeaturesContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Users, Search, Filter, Calendar, Download, Upload, PlusCircle, Edit2,
  Trash2, GraduationCap, Clock, FileText, X, ChevronDown, TrendingUp, Award,
  FileCheck, BookOpen, UserCheck, Bell, MessageSquare, Settings, BarChart3,
  Wallet, DollarSign, CreditCard, ClipboardList, CheckCircle, AlertTriangle, XCircle, Handshake, Eye
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import NewEmployeeForm from './NewEmployeeForm';
import EmployeesList from './EmployeesList';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import TeacherClassAssignment from './TeacherClassAssignment';
import NotificationsPanel from './NotificationsPanel';
import { notificationsData } from '../data/notificationsData';

import { tasksData, usersData } from '../data/tasksData';
import AttendanceTracking from './AttendanceTracking';
import OrganigrammeRH from './OrganigrammeRH';
//import VariableElements from './VariableElements';

// Les onglets qu'une adresse peut demander par `?tab=`. `liste` est le nom
// court des liens directs (`?tab=liste&photo=sans`, panneau des alertes : la
// liste des employés en lit le reste) ; les noms internes restent acceptés.
const ONGLETS_PAR_ADRESSE = { liste: 'employee-list' };
const ONGLETS_RH = ['dashboard', 'employee-list', 'new-employee', 'assignment', 'tracking'];

const lireOngletDemande = (search, pointageOuvert) => {
  const demande = new URLSearchParams(search || '').get('tab');
  const onglet = ONGLETS_PAR_ADRESSE[demande] || demande;
  if (!ONGLETS_RH.includes(onglet)) return null;
  if (onglet === 'tracking' && !pointageOuvert) return null;
  return onglet;
};


const HRManagement = () => {
  // Le pointage est un module vendu separement. Une ecole Essentiel a les RH
  // — indispensables a l emploi du temps, qui suppose des enseignants crees et
  // affectes — mais pas le pointage.
  const { estActive } = useFeatures();
  const pointageOuvert = estActive('module.pointage');
  const location = useLocation();
  const typeNavigation = useNavigationType();

  // États
  const [activeYear, setActiveYear] = useState(null);
  const [, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  // Un lien profond (`?tab=liste`) choisit l'onglet d'arrivée ; sans lui, le
  // tableau de bord, comme avant.
  const [selectedMainTab, setSelectedMainTab] = useState(
    () => lireOngletDemande(location.search, pointageOuvert) || 'dashboard'
  );

  // Un lien profond ouvert pendant que l'écran est déjà là doit être suivi
  // (même motif que GestionComptable) : on suit chaque NOUVELLE navigation,
  // reconnue à sa clé, et non le changement de `tab`, qui reste souvent dans
  // l'adresse après un premier lien. Un clic sur un onglet ne navigue pas : il
  // n'est jamais annulé par l'adresse restée en arrière. Un remplacement
  // d'adresse n'est pas une demande de l'agent (la liste retire `photo` une
  // fois le lien appliqué) : on l'ignore. Le montage est réglé par l'état initial.
  const derniereNavigation = useRef(location.key);
  useEffect(() => {
    if (location.key === derniereNavigation.current) return;
    derniereNavigation.current = location.key;
    if (typeNavigation === NavigationType.Replace) return;
    const onglet = lireOngletDemande(location.search, pointageOuvert);
    if (onglet) setSelectedMainTab(onglet);
  }, [location.key, location.search, typeNavigation, pointageOuvert]);

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [, setIsLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [tasks, setTasks] = useState(tasksData);
  const [currentUser, setCurrentUser] = useState(usersData[0]);
  const [notifications, setNotifications] = useState(notificationsData);
  const [toastInfo, setToastInfo] = useState(null);

  // Nouveaux états pour les statistiques réelles
  const [dashboardStats, setDashboardStats] = useState({
    totalActifs: 0,
    enseignement: 0,
    administration: 0,
    direction: 0,
    supportTechnique: 0,
    anomalies: {}
  });

  const showToastMessage = (message, type = 'success', options = {}) => {
    if (!message) return;
    setToastInfo({ message, type, ...options });
  };

  const showErrorToast = (message, options = {}) => {
    showToastMessage(message, 'error', options);
  };

  // Fonction pour charger toutes les données
  const loadData = async () => {
    try {
      setIsLoading(true);
      const activeYearResponse = await api.anneesScolaires.getActive();

      if (!activeYearResponse.success || !activeYearResponse.data) {
        const message = "Aucune ann\u00e9e scolaire active n'est d\u00e9finie";
        setError(message);
        showErrorToast(message);
        return;
      }

      setActiveYear(activeYearResponse.data);
      const statsPromise = loadDashboardStats();

      const [employeesResult, rolesResult, departementsResult] = await Promise.allSettled([
        api.employes.getAll(activeYearResponse.data.id),
        api.roles.getAll(),
        api.departements.getAll()
      ]);

      if (employeesResult.status === 'fulfilled') {
        const employeesRes = employeesResult.value;
        setEmployees(Array.isArray(employeesRes) ? employeesRes : (employeesRes?.data || []));
      } else {
        console.error('Erreur lors du chargement des employ\u00e9s:', employeesResult.reason);
        showErrorToast(`Erreur lors du chargement des employ\u00e9s: ${employeesResult.reason?.message || 'erreur inconnue'}`);
      }

      if (rolesResult.status === 'fulfilled') {
        const rolesRes = rolesResult.value;
        setRoles(Array.isArray(rolesRes) ? rolesRes : (rolesRes?.data || []));
      } else {
        console.error('Erreur lors du chargement des r\u00f4les:', rolesResult.reason);
      }

      if (departementsResult.status === 'fulfilled') {
        const departementsRes = departementsResult.value;
        setDepartements(Array.isArray(departementsRes) ? departementsRes : (departementsRes?.data || []));
      } else {
        console.error('Erreur lors du chargement des d\u00e9partements:', departementsResult.reason);
      }

      await statsPromise;
    } catch (error) {
      console.error('Erreur:', error);
      const message = `Erreur lors du chargement des donn\u00e9es RH: ${error.message}`;
      setError(message);
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  const normalizeRhStats = (payload = {}) => {
    const categories = Array.isArray(payload.categories) ? payload.categories : [];
    const findTotal = (key) => {
      const category = categories.find(item => item.key === key);
      return Number(category?.total || 0);
    };

    return {
      totalActifs: Number(payload.total_actifs || 0),
      enseignement: findTotal('ENSEIGNEMENT'),
      administration: findTotal('ADMINISTRATION'),
      direction: findTotal('DIRECTION'),
      supportTechnique: findTotal('SUPPORT_TECHNIQUE'),
      anomalies: payload.anomalies || {}
    };
  };

  // Nouvelle fonction pour charger les statistiques du tableau de bord
  const loadDashboardStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.employes.getRhStats();

      if (response.success && response.data) {
        setDashboardStats(normalizeRhStats(response.data));
        return;
      }

      throw new Error(response.message || 'Statistiques RH indisponibles');
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques RH:', error);
      const message = `Erreur lors du chargement des statistiques RH: ${error.message}`;
      showErrorToast(message);
      setDashboardStats({
        totalActifs: 0,
        enseignement: 0,
        administration: 0,
        direction: 0,
        supportTechnique: 0,
        anomalies: {}
      });
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);


  const COLORS = ['#246990', '#c66d59', '#319190', '#4B5563'];

  // Données des statistiques RH multi-tenant
  const stats = [
    {
      title: 'Total employés actifs',
      value: statsLoading ? '...' : dashboardStats.totalActifs.toString(),
      icon: Users,
      color: 'from-[#246990] to-[#3084b5]'
    },
    {
      title: 'Enseignants',
      value: statsLoading ? '...' : dashboardStats.enseignement.toString(),
      icon: GraduationCap,
      color: 'from-[#319190] to-[#63b5b4]'
    },
    {
      title: 'Administration / Accueil',
      value: statsLoading ? '...' : dashboardStats.administration.toString(),
      icon: Users,
      color: 'from-[#c66d59] to-[#ae7467]'
    },
    {
      title: 'Direction',
      value: statsLoading ? '...' : dashboardStats.direction.toString(),
      icon: UserCheck,
      color: 'from-[#8B5CF6] to-[#A855F7]'
    },
    {
      title: 'Support / Technique',
      value: statsLoading ? '...' : dashboardStats.supportTechnique.toString(),
      icon: Settings,
      color: 'from-gray-800 to-gray-700'
    }
  ];

  // Gestion du modal pour ajouter un employé
  const handleAddEmployee = () => {
    setShowAddModal(true);
  };

  const handleEmployeeUpdate = (updatedEmployee) => {
    console.log('Mise à jour reçue dans HRManagement:', updatedEmployee);

    setEmployees(prevEmployees => {
      if (!prevEmployees) return prevEmployees;
      return prevEmployees.map(emp =>
        emp.id === updatedEmployee.id ? { ...emp, ...updatedEmployee } : emp
      );
    });

    // Recharger les statistiques après une mise à jour
    loadDashboardStats();
  }

  const handleEmployeesListRefresh = (newEmployeesList) => {
    console.log('Nouvelle liste reçue dans HRManagement');
    setEmployees(newEmployeesList);

    // Recharger les statistiques après un refresh
    loadDashboardStats();
  };

  const handleNotificationClick = (notification) => {
    setNotifications(prev => prev.map(n =>
      n.id === notification.id ? { ...n, read: true } : n
    ));

    setSelectedMainTab(notification.target.tab);
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleArchiveNotification = (notification) => {
    setNotifications(prev => prev.map(n =>
      n.id === notification.id ? { ...n, archived: true, read: true } : n
    ));
  };

  const handleUnarchiveNotification = (notification) => {
    setNotifications(prev => prev.map(n =>
      n.id === notification.id ? { ...n, archived: false } : n
    ));
  };

  const handleAddTask = (newTask) => {
    setTasks([...tasks, newTask]);
  };

  const handleUpdateTask = (taskId, updates) => {
    setTasks(tasks.map(task =>
      task.id === taskId ? { ...task, ...updates } : task
    ));
  };

  const handleDeleteTask = (taskId) => {
    setTasks(tasks.filter(task => task.id !== taskId));
  };

  const handleAssignTask = (taskId, userId) => {
    setTasks(tasks.map(task =>
      task.id === taskId ? { ...task, assignedTo: userId } : task
    ));
  };

  return (
    <div className="w-full p-4">
      <h1 className="text-3xl font-bold mb-6 text-[#246990]">Ressources Humaines</h1>

      {/* Navigation principale avec le même style que ComptabiliteEtPaie */}
      <Tabs
        defaultValue="dashboard"
        value={selectedMainTab}
        onValueChange={setSelectedMainTab}
        className="w-full"
      >
        {/* Le nombre de colonnes suit le nombre d'onglets visibles : figé à
            cinq, masquer « Présence/Temps » laisserait une colonne vide. */}
        <TabsList className={`grid ${pointageOuvert ? 'grid-cols-5' : 'grid-cols-4'} mb-8 bg-[#246990]/10`}>
          <TabsTrigger
            value="dashboard"
            className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
          >
            <BarChart3 size={16} />
            <span className="hidden md:inline">Tableau de bord</span>
          </TabsTrigger>

          <TabsTrigger
            value="employee-list"
            className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
          >
            <Users size={16} />
            <span className="hidden md:inline">Liste employés</span>
          </TabsTrigger>

          <TabsTrigger
            value="new-employee"
            className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
          >
            <PlusCircle size={16} />
            <span className="hidden md:inline">Nouvel employé</span>
          </TabsTrigger>

          <TabsTrigger
            value="assignment"
            className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
          >
            <BookOpen size={16} />
            <span className="hidden md:inline">Affectations</span>
          </TabsTrigger>

          {/* Le pointage est vendu à part : une école Essentiel gère ses
              employés et leurs affectations, mais ne pointe pas. */}
          {pointageOuvert && (
            <TabsTrigger
              value="tracking"
              className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
            >
              <Clock size={16} />
              <span className="hidden md:inline">Présence/Temps</span>
            </TabsTrigger>
          )}


          {/* <TabsTrigger 
            value="variables" 
            className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
          >
            <FileText size={16} />
            <span className="hidden md:inline">Elements variables</span>
          </TabsTrigger>
          
          
          {/* <TabsTrigger 
            value="documents" 
            className="flex items-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white"
          >
            <FileText size={16} />
            <span className="hidden md:inline">Documents</span>
          </TabsTrigger> */}

        </TabsList>

        {/* Contenu de l'onglet Tableau de bord */}
        <TabsContent value="dashboard">
          {/* Statistiques - 5 blocs avec grille adaptée */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {stats.map((stat, index) => (
              <Card key={index} className="border-0 shadow-md overflow-hidden">
                <div className={`bg-gradient-to-r ${stat.color} text-white p-4`}>
                  <CardTitle className="text-sm font-medium flex items-center gap-1 mb-2">
                    <stat.icon size={16} className="text-white" />
                    <span className="text-xs leading-tight">{stat.title}</span>
                  </CardTitle>
                  <CardDescription className="text-white/80 text-xs hidden md:block">Stats actuelles</CardDescription>
                  {statsLoading ? (
                    <div className="h-12 flex items-center justify-center">
                      <div className="animate-pulse h-3 bg-white/30 rounded w-3/4"></div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center mt-2">
                      <span className="text-2xl font-bold">{stat.value}</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {/* Organigramme */}
          <OrganigrammeRH />


          {/* Graphiques supplémentaires */}


        </TabsContent>

        {/* Contenu des autres onglets */}
        <TabsContent value="employee-list">
          <EmployeesList
            onEmployeeUpdate={handleEmployeeUpdate}
            onEmployeesListRefresh={handleEmployeesListRefresh}
          />
        </TabsContent>

        <TabsContent value="new-employee">
          <NewEmployeeForm />
        </TabsContent>

        <TabsContent value="assignment">
          <TeacherClassAssignment />
        </TabsContent>

        {pointageOuvert && (
          <TabsContent value="tracking">
            <AttendanceTracking employees={employees} />
          </TabsContent>
        )}

        {/* <TabsContent value="variables">
          <VariableElements />
        </TabsContent> */}

      </Tabs>
      {toastInfo && (
        shouldUseLongToast(toastInfo.message, toastInfo.type, toastInfo) ? (
          <LongToast
            show
            title={toastInfo.title}
            message={toastInfo.message}
            type={toastInfo.type}
            onClose={() => setToastInfo(null)}
          />
        ) : (
          <Toast
            message={toastInfo.message}
            type={toastInfo.type}
            onClose={() => setToastInfo(null)}
          />
        )
      )}
    </div>
  );
};

export default HRManagement;
